/**
 * 持仓计算（与小程序 holding-calc.js 口径对齐）
 */

export const EPS = 1e-6;
export const ZERO_SHARES_THRESHOLD = 0.01;

function round6(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 1e6) / 1e6;
}

function round2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 100) / 100;
}

/**
 * 是否清仓尾差份额。
 * - 小程序 recalc：shares < 0.01 归零（正好 0.01 会留下，恢复后常见「持有金额 0.01」）
 * - 网页更严：<=0.01（含浮点）以及「展示到分后仍 ≤0.01」的残渣一并视为已清仓
 */
export function isShareDust(shares) {
  const sh = Number(shares);
  if (!Number.isFinite(sh) || sh <= 0) return true;
  if (sh <= ZERO_SHARES_THRESHOLD + 1e-8) return true;
  if (round6(sh) <= ZERO_SHARES_THRESHOLD) return true;
  // 旧数据赎回按 2 位留下的 0.01x，界面持有金额常显示成 0.01
  if (round2(sh) <= ZERO_SHARES_THRESHOLD) return true;
  return false;
}

/** 份额尾差归零（<=0.01 视为清仓，含浮点与分位展示残渣） */
export function snapShareDust(shares) {
  const sh = Number(shares);
  if (!Number.isFinite(sh) || sh <= 0) return 0;
  if (isShareDust(sh)) return 0;
  return round6(sh);
}

export function maxRedeemConfirmYmd(trades) {
  let maxD = '';
  (trades || []).forEach((t) => {
    if (!t || isBuyTrade(t) || !isTradeConfirmed(t)) return;
    const d = normYmd(t.confirmDate);
    if (d && (!maxD || d > maxD)) maxD = d;
  });
  return maxD;
}

export function normYmd(s) {
  const t = String(s || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : '';
}

export function isBuyTrade(trade) {
  const type = trade?.type == null ? '' : String(trade.type);
  return type === 'buy' || type === '买入';
}

export function isTradeConfirmed(trade) {
  if (!trade) return false;
  const st = trade.status != null ? String(trade.status) : 'confirmed';
  if (st === 'pending') return false;
  if (!isBuyTrade(trade)) {
    return !!(normYmd(trade.confirmDate) && Number(trade.confirmNav) > 0);
  }
  return !!(normYmd(trade.confirmDate) && Number(trade.confirmNav) > 0);
}

export function isPendingBuyTrade(trade) {
  return isBuyTrade(trade) && !isTradeConfirmed(trade);
}

export function isRedeemTrade(trade) {
  return !isBuyTrade(trade);
}

export function isPendingRedeemTrade(trade) {
  return isRedeemTrade(trade) && !isTradeConfirmed(trade);
}

export function tradeAccrualDate(trade) {
  const nav = normYmd(trade?.confirmNavSourceDate);
  if (nav) return nav;
  return normYmd(trade?.confirmDate) || '';
}

export function tradeShares(trade) {
  const amount = Number(trade?.amount) || 0;
  const nav = Number(trade?.confirmNav);
  if (!Number.isFinite(nav) || nav <= 0) return 0;
  return Math.round((amount / nav) * 1e6) / 1e6;
}

function isBuyIncludedForSharesAsOf(trade, asOfYmd) {
  const d = normYmd(asOfYmd);
  if (!d || !isBuyTrade(trade)) return false;
  const accrual = tradeAccrualDate(trade);
  const confirm = normYmd(trade?.confirmDate);
  if (!accrual || accrual > d) return false;
  if (accrual < d) return true;
  return !!(confirm && confirm <= d);
}

function isRedeemIncludedForSharesAsOf(trade, asOfYmd) {
  const d = normYmd(asOfYmd);
  if (!d || isBuyTrade(trade)) return false;
  if (!isTradeConfirmed(trade)) return false;
  const confirm = normYmd(trade?.confirmDate);
  if (confirm) return confirm <= d;
  const accrual = tradeAccrualDate(trade);
  return !!(accrual && accrual <= d);
}

/**
 * 赎回（日收益份额回放）：有净值日时从「净值日 + 1 自然日」起减份额（净值日当天仍持有）；
 * 无 confirmNavSourceDate 时与 isRedeemIncludedForSharesAsOf 一致。
 */
export function isRedeemIncludedForDailyPnlAsOf(trade, asOfYmd) {
  const d = normYmd(asOfYmd);
  if (!d || isBuyTrade(trade)) return false;
  if (!isTradeConfirmed(trade)) return false;
  const navYmd = normYmd(trade?.confirmNavSourceDate);
  if (navYmd) {
    const from = addDaysYmd(navYmd, 1);
    return !!(from && from <= d);
  }
  return isRedeemIncludedForSharesAsOf(trade, asOfYmd);
}

export function isTradeIncludedForSharesAsOf(trade, asOfYmd) {
  if (isBuyTrade(trade)) return isBuyIncludedForSharesAsOf(trade, asOfYmd);
  return isRedeemIncludedForSharesAsOf(trade, asOfYmd);
}

/** 日收益链：买入同 sharesAsOfDate；赎回见 isRedeemIncludedForDailyPnlAsOf */
export function isTradeIncludedForDailyPnlAsOf(trade, asOfYmd) {
  if (isBuyTrade(trade)) return isBuyIncludedForSharesAsOf(trade, asOfYmd);
  return isRedeemIncludedForDailyPnlAsOf(trade, asOfYmd);
}

export function hasConfirmedBuyOnOrBefore(trades, asOfYmd) {
  const d = normYmd(asOfYmd);
  if (!d) return false;
  const arr = Array.isArray(trades) ? trades : [];
  for (let i = 0; i < arr.length; i += 1) {
    const t = arr[i];
    if (!isBuyTrade(t)) continue;
    const confirm = normYmd(t?.confirmDate);
    if (confirm && confirm <= d) return true;
  }
  return false;
}

export function isActiveHolding(trades, latestNav) {
  const arr = Array.isArray(trades) ? trades : [];
  if (!arr.length) return false;
  if (isHoldingOnlyPending(arr)) return false;
  return !isRedeemed(recalcHolding(arr, latestNav));
}

/**
 * 资产中心「日收益明细」是否展示该产品（与「未更新」展示无关）
 *
 * 普通持仓 / 纯全赎：
 * - 用 sharesAsOfDate（赎回 confirm<=D），与改「周期切开」之前网页口径一致
 * - 全部赎回确认日当天份额已为 0 → 不进日明细（避免「已赎回 + 未更新」空壳）
 *
 * 全赎后再买（选中日之后还有确认买入）：
 * - 赎回按 confirm<=D 扣光，买入仍 confirm<D，避免旧仓在清仓日占「未更新」
 */
export function shouldShowInDayProfitDetail(trades, dayKey) {
  const d = normYmd(dayKey);
  if (!d) return false;
  if (!hasConfirmedBuyOnOrBefore(trades, d)) return false;

  const arr = Array.isArray(trades) ? trades : [];
  let hasLaterBuy = false;
  for (let i = 0; i < arr.length; i += 1) {
    const t = arr[i];
    if (!isBuyTrade(t) || !isTradeConfirmed(t)) continue;
    const c = normYmd(t?.confirmDate);
    if (c && c > d) {
      hasLaterBuy = true;
      break;
    }
  }

  if (hasLaterBuy) {
    const settled = arr.filter((t) => {
      if (!isTradeConfirmed(t)) return false;
      const confirm = normYmd(t?.confirmDate);
      if (!confirm) return false;
      if (isBuyTrade(t)) return confirm < d;
      return confirm <= d;
    });
    return sharesAsOfDate(settled, d) > EPS;
  }

  return sharesAsOfDate(trades, d) > EPS;
}

export function sortTrades(trades) {
  return (trades || []).slice().sort((a, b) => {
    const da = tradeAccrualDate(a).localeCompare(tradeAccrualDate(b));
    if (da !== 0) return da;
    const dc = (a.confirmDate || '').localeCompare(b.confirmDate || '');
    if (dc !== 0) return dc;
    const aBuy = isBuyTrade(a) ? 0 : 1;
    const bBuy = isBuyTrade(b) ? 0 : 1;
    if (aBuy !== bBuy) return aBuy - bBuy;
    return (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0);
  });
}

export function firstAccrualDate(trades) {
  let min = '';
  const arr = Array.isArray(trades) ? trades : [];
  for (let i = 0; i < arr.length; i += 1) {
    const t = arr[i];
    if (!isBuyTrade(t) || !isTradeConfirmed(t)) continue;
    const ad = tradeAccrualDate(t);
    if (!ad) continue;
    if (!min || ad < min) min = ad;
  }
  return min || null;
}

/**
 * 最后一次「确认后份额归零」的赎回确认日（持仓周期分界，该日结束上一段）。
 */
export function lastFullRedeemConfirmYmd(trades) {
  const sorted = sortTrades(trades);
  let shares = 0;
  let lastCut = '';
  for (let i = 0; i < sorted.length; i += 1) {
    const t = sorted[i];
    if (!isTradeConfirmed(t)) continue;
    const sh = t.shares != null ? Number(t.shares) : tradeShares(t);
    if (!Number.isFinite(sh) || sh < 0) continue;
    if (isBuyTrade(t)) {
      shares += sh;
    } else {
      shares -= Math.min(sh, shares);
      if (isShareDust(shares)) {
        shares = 0;
        const c = normYmd(t?.confirmDate);
        if (c) lastCut = c;
      }
    }
  }
  return lastCut || '';
}

/**
 * 当前持仓周期内的交易：全赎确认日之后的已确认单 + 全部在途单。
 * 无全赎分界则返回全部。
 */
export function tradesInCurrentCycle(trades) {
  const arr = Array.isArray(trades) ? trades : [];
  const cut = lastFullRedeemConfirmYmd(arr);
  if (!cut) return arr.slice();
  return arr.filter((t) => {
    if (!isTradeConfirmed(t)) return true;
    const c = normYmd(t?.confirmDate);
    return !!(c && c > cut);
  });
}

/** 上一已结束周期内的已确认交易（用于已清仓展示起息日） */
export function tradesInLastClosedCycle(trades) {
  const arr = Array.isArray(trades) ? trades : [];
  const cut = lastFullRedeemConfirmYmd(arr);
  if (!cut) return [];
  const sorted = sortTrades(arr);
  let shares = 0;
  let prevCut = '';
  for (let i = 0; i < sorted.length; i += 1) {
    const t = sorted[i];
    if (!isTradeConfirmed(t)) continue;
    const sh = t.shares != null ? Number(t.shares) : tradeShares(t);
    if (!Number.isFinite(sh) || sh < 0) continue;
    if (isBuyTrade(t)) {
      shares += sh;
    } else {
      shares -= Math.min(sh, shares);
      if (isShareDust(shares)) {
        shares = 0;
        const c = normYmd(t?.confirmDate);
        if (c && c < cut) prevCut = c;
        if (c === cut) break;
      }
    }
  }
  return arr.filter((t) => {
    if (!isTradeConfirmed(t)) return false;
    const c = normYmd(t?.confirmDate);
    if (!c || c > cut) return false;
    if (prevCut && c <= prevCut) return false;
    return true;
  });
}

/**
 * 是否「仅待确认持仓」（当前周期）：有待确认买入、确认后份额≈0、且当前周期尚无已确认买入。
 * 全赎清仓后再录待确认 = 新开在途，算仅待确认；历史持有日展示改由 shouldShowInDayProfitDetail 按日回放。
 */
export function isHoldingOnlyPending(trades) {
  const arr = Array.isArray(trades) ? trades : [];
  if (!arr.some((t) => isPendingBuyTrade(t))) return false;
  const calc = recalcHolding(arr, null);
  if (calc.shares > EPS) return false;
  const cycle = tradesInCurrentCycle(arr);
  return !cycle.some((t) => isBuyTrade(t) && isTradeConfirmed(t));
}

export function pendingRedeemSharesLocked(trades, excludeTradeId) {
  const arr = Array.isArray(trades) ? trades : [];
  const ex = excludeTradeId != null ? String(excludeTradeId) : '';
  let locked = 0;
  for (let i = 0; i < arr.length; i += 1) {
    const t = arr[i];
    if (!isPendingRedeemTrade(t)) continue;
    if (ex && String(t.tradeId) === ex) continue;
    const sh = t.shares != null ? Number(t.shares) : 0;
    if (Number.isFinite(sh) && sh > 0) locked += sh;
  }
  return Math.round(locked * 1e6) / 1e6;
}

export function availableRedeemShares(trades, excludeTradeId) {
  const calc = recalcHolding(Array.isArray(trades) ? trades : [], null);
  const total = calc.shares != null ? Number(calc.shares) : 0;
  const locked = pendingRedeemSharesLocked(trades, excludeTradeId);
  const avail = total - locked;
  return avail > EPS ? Math.round(avail * 1e6) / 1e6 : 0;
}

export function isRedeemed(calc) {
  return calc?.shares != null && isShareDust(calc.shares);
}

/** 表单展示用 2 位份额（与小程序 displayShares2 对齐） */
export function displayShares2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '';
  return (Math.round(x * 100) / 100).toFixed(2);
}

/** 份额比较用整数微单位（与小程序 toInt1e6 对齐，避免浮点/展示两位误报超限） */
export function toInt1e6(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return NaN;
  return Math.round(x * 1e6);
}

/** 全部赎回时：请求份额接近可赎上限则写满，避免 0.01 尾差 */
export function resolveRedeemSharesForSave(trades, requestedShares, excludeTradeId) {
  const req = round6(Number(requestedShares));
  if (!(req > 0)) return req;
  const avail = availableRedeemShares(trades, excludeTradeId);
  if (!(avail > 0)) return req;
  if (req >= avail - ZERO_SHARES_THRESHOLD) return round6(avail);
  return req;
}

/**
 * 校验并解析赎回份额（对齐小程序：全部模式写满 avail；手填经 resolve + toInt1e6 比较）
 * @returns {{ ok: boolean, shares: number, avail: number, error?: string }}
 */
export function prepareRedeemSharesForSave(trades, requestedShares, excludeTradeId, options = {}) {
  const redeemAllMode = !!options.redeemAllMode;
  const avail = availableRedeemShares(trades, excludeTradeId);
  let sharesInput = 0;
  if (redeemAllMode) {
    sharesInput = avail > EPS ? round6(avail) : 0;
  } else {
    const raw = Number(requestedShares);
    if (!(raw > 0)) {
      return { ok: false, shares: 0, avail, error: '请填写赎回份额' };
    }
    sharesInput = resolveRedeemSharesForSave(trades, raw, excludeTradeId);
  }
  if (!(sharesInput > 0)) {
    return {
      ok: false,
      shares: 0,
      avail,
      error: redeemAllMode ? '暂无可赎份额' : '请填写赎回份额'
    };
  }
  const in1e6 = toInt1e6(sharesInput);
  const max1e6 = toInt1e6(avail);
  if (Number.isFinite(in1e6) && Number.isFinite(max1e6) && in1e6 > max1e6) {
    const t2 = displayShares2(avail) || Number(avail).toFixed(2);
    return {
      ok: false,
      shares: sharesInput,
      avail,
      error: `赎回份额不能超过可赎上限 ${t2}`
    };
  }
  return { ok: true, shares: round6(sharesInput), avail };
}

/** 修正历史赎回少记导致的尾差（含正好 0.01 / 浮点略超 / 分位展示为 0.01） */
export function normalizeRedeemDustInTrades(trades) {
  const arr = (Array.isArray(trades) ? trades : []).map((t) => (t ? { ...t } : t));
  const calc = recalcHolding(arr, null, null, { skipDustSnap: true });
  if (!(calc.shares > EPS)) return { trades: arr, changed: false };
  if (!isShareDust(calc.shares)) return { trades: arr, changed: false };

  const dust = round6(calc.shares);
  const sorted = sortTrades(arr);
  let lastRedeemId = '';
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const t = sorted[i];
    if (isRedeemTrade(t) && isTradeConfirmed(t)) {
      lastRedeemId = String(t.tradeId || '');
      break;
    }
  }
  if (!lastRedeemId) return { trades: arr, changed: false };

  let changed = false;
  const next = arr.map((t) => {
    if (!t || String(t.tradeId) !== lastRedeemId) return t;
    const oldSh = Number(t.shares) || 0;
    const newSh = round6(oldSh + dust);
    if (Math.abs(newSh - oldSh) < EPS) return t;
    changed = true;
    const nav = Number(t.confirmNav) || 0;
    return {
      ...t,
      shares: newSh,
      amount: nav > 0 ? round2(newSh * nav) : t.amount
    };
  });
  return { trades: next, changed };
}

export function recalcHolding(trades, latestNav, asOfYmd, opts) {
  const skipDustSnap = !!(opts && opts.skipDustSnap);
  const sorted = sortTrades(trades);
  let shares = 0;
  let cost = 0;
  let avgCost = 0;
  let realizedProfit = 0;
  let totalInvest = 0;
  let totalRedeem = 0;

  sorted.forEach((trade) => {
    if (!isTradeConfirmed(trade)) return;
    const sh = trade.shares != null ? Number(trade.shares) : tradeShares(trade);
    const amount = Number(trade.amount) || 0;

    if (isBuyTrade(trade)) {
      totalInvest += amount;
      const newShares = shares + sh;
      const newCost = cost + amount;
      avgCost = newShares > 0 ? newCost / newShares : 0;
      shares = newShares;
      cost = newCost;
      return;
    }

    totalRedeem += amount;
    if (shares < EPS) return;
    const redeemShares = Math.min(sh, shares);
    const costOut = avgCost * redeemShares;
    realizedProfit += amount - costOut;
    shares -= redeemShares;
    cost -= costOut;
    if (!skipDustSnap && isShareDust(shares)) {
      shares = 0;
      cost = 0;
      avgCost = 0;
    } else {
      avgCost = shares > 0 ? cost / shares : 0;
    }
  });

  if (!skipDustSnap) {
    shares = snapShareDust(shares);
    if (!(shares > EPS)) {
      shares = 0;
      cost = 0;
      avgCost = 0;
    }
  }

  // 起息日：持有中用当前周期；已清仓用上一结束周期（避免全赎再买叠到旧起息日）
  let accrualFirst = firstAccrualDate(tradesInCurrentCycle(sorted));
  if (!accrualFirst && !(shares > EPS)) {
    accrualFirst = firstAccrualDate(tradesInLastClosedCycle(sorted)) || firstAccrualDate(sorted);
  }

  const nav = latestNav != null && Number(latestNav) > 0 ? Number(latestNav) : null;
  const marketValue = nav != null && shares >= 0 ? shares * nav : null;
  const unrealizedProfit = marketValue != null && cost != null ? marketValue - cost : null;
  const rate = cost > 0 && unrealizedProfit != null ? unrealizedProfit / cost : null;

  return {
    shares,
    cost,
    avgCost,
    realizedProfit,
    totalInvest,
    totalRedeem,
    marketValue,
    unrealizedProfit,
    rate,
    firstBuyDate: accrualFirst,
    firstAccrualDate: accrualFirst,
    isRedeemed: isShareDust(shares)
  };
}

export function sharesAsOfDate(trades, dateStr) {
  if (!Array.isArray(trades) || !dateStr) return 0;
  const d = normYmd(dateStr);
  if (!d) return 0;
  const included = (trades || []).filter((t) => isTradeIncludedForSharesAsOf(t, d));
  const calc = recalcHolding(included, null);
  return snapShareDust(calc.shares != null ? calc.shares : 0);
}

/** 某日收盘份额（日收益 / profit-day-cache；赎回按净值日次日起扣） */
export function sharesAsOfDateForDailyPnl(trades, dateStr) {
  if (!Array.isArray(trades) || !dateStr) return 0;
  const d = normYmd(dateStr);
  if (!d) return 0;
  const included = (trades || []).filter((t) => isTradeIncludedForDailyPnlAsOf(t, d));
  const calc = recalcHolding(included, null);
  return snapShareDust(calc.shares != null ? calc.shares : 0);
}

export function sharesForDailyPnl(trades, navDayYmd) {
  const d = normYmd(navDayYmd);
  if (!d) return 0;
  const sh = sharesAsOfDateForDailyPnl(trades, d);
  return sh > EPS ? sh : 0;
}

export function tradesEffectiveOnOrBefore(trades, dateStr) {
  const d = normYmd(dateStr);
  if (!d) return [];
  return (trades || []).filter((t) => isTradeIncludedForSharesAsOf(t, d));
}

export function tradeEffectiveDateForShares(trade) {
  if (isBuyTrade(trade)) return tradeAccrualDate(trade);
  const confirm = normYmd(trade?.confirmDate);
  if (confirm) return confirm;
  return tradeAccrualDate(trade);
}

function addDaysYmd(ymd, deltaDays) {
  const d = normYmd(ymd);
  if (!d) return '';
  const n = Number(deltaDays);
  if (!Number.isFinite(n) || n === 0) return d;
  const dt = new Date(d.replace(/-/g, '/'));
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export function tradeCashFlowDateForXirr(trade) {
  if (isBuyTrade(trade)) {
    const payment = normYmd(trade?.paymentDate);
    if (payment) return payment;
    return tradeAccrualDate(trade);
  }
  const confirm = normYmd(trade?.confirmDate);
  if (confirm) return confirm;
  return tradeAccrualDate(trade);
}

export function buildProductXirrFlows(trades, terminalNavDate, terminalMv) {
  const flows = [];
  const arr = Array.isArray(trades) ? trades : [];
  arr.forEach((t) => {
    if (!isTradeConfirmed(t)) return;
    const dt = tradeCashFlowDateForXirr(t);
    const amt = t?.amount != null ? Number(t.amount) : NaN;
    if (!dt || !Number.isFinite(amt) || !(amt > 0)) return;
    if (isBuyTrade(t)) flows.push({ date: dt, amount: -amt });
    else flows.push({ date: dt, amount: amt });
  });
  const tv = Number(terminalMv);
  let termDate = normYmd(terminalNavDate);
  const fa = firstAccrualDate(arr);
  if (fa && termDate && termDate < fa) termDate = fa;
  if (termDate && Number.isFinite(tv) && tv > 0) {
    const termFlowDate = addDaysYmd(termDate, 1) || termDate;
    flows.push({ date: termFlowDate, amount: tv });
  }
  flows.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  return flows;
}

function parseDateForXIRR(str) {
  if (!str || typeof str !== 'string') return null;
  const s = String(str).trim();
  if (/^\d{4}-\d{1,2}-\d{1,2}(?:T|$)/.test(s)) return new Date(s);
  const parts = s.split(/[-/]/);
  if (parts.length >= 2 && parts[0].length <= 2 && parts[1].length <= 2) {
    const y = new Date().getFullYear();
    const normalized = `${y}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    return new Date(normalized);
  }
  return new Date(s);
}

export function calcXIRR(cashFlows) {
  if (!Array.isArray(cashFlows) || cashFlows.length < 2) return null;
  const sorted = cashFlows.slice().sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  const base = sorted[0];
  const baseDate = base?.date ? parseDateForXIRR(base.date) : null;
  if (!baseDate || Number.isNaN(baseDate.getTime())) return null;

  const flows = [];
  sorted.forEach((f) => {
    const date = f.date ? parseDateForXIRR(f.date) : null;
    const amount = Number(f.amount);
    if (!date || Number.isNaN(date.getTime()) || Number.isNaN(amount)) return;
    const years = (date.getTime() - baseDate.getTime()) / (24 * 3600 * 1000) / 365;
    flows.push({ years, amount });
  });
  if (flows.length < 2) return null;
  if (!flows.some((f) => f.amount > 0) || !flows.some((f) => f.amount < 0)) return null;

  const npv = (r) => {
    let s = 0;
    for (let i = 0; i < flows.length; i += 1) {
      const factor = (1 + r) ** flows[i].years;
      if (factor <= 0 || !Number.isFinite(factor)) return NaN;
      s += flows[i].amount / factor;
    }
    return s;
  };

  const npvPrime = (r) => {
    let s = 0;
    for (let i = 0; i < flows.length; i += 1) {
      const factor = (1 + r) ** (flows[i].years + 1);
      if (factor <= 0 || !Number.isFinite(factor)) return NaN;
      s -= flows[i].years * flows[i].amount / factor;
    }
    return s;
  };

  let r = 0.1;
  for (let i = 0; i < 100; i += 1) {
    if (r <= -1) return null;
    const v = npv(r);
    const vp = npvPrime(r);
    if (!Number.isFinite(v) || !Number.isFinite(vp) || Math.abs(vp) < 1e-12) return null;
    const rNew = r - v / vp;
    if (Math.abs(rNew - r) < 1e-7) return rNew;
    if (rNew <= -1) return null;
    r = rNew;
  }
  return null;
}

export function calcHoldAnnualRate(trades, terminalNavDate, terminalMv) {
  const flows = buildProductXirrFlows(trades, terminalNavDate, terminalMv);
  const xirrDec = flows.length >= 2 ? calcXIRR(flows) : null;
  if (xirrDec != null && Number.isFinite(Number(xirrDec))) return Number(xirrDec);
  return null;
}

export function buildGroupAvgWanshou(pnlSum, mvSum) {
  const p = Number(pnlSum);
  const m = Number(mvSum);
  if (!Number.isFinite(p) || !Number.isFinite(m) || !(m > 0)) {
    return { text: '', signClass: 'zero', raw: NaN };
  }
  const w = (p / m) * 10000;
  if (!Number.isFinite(w)) return { text: '', signClass: 'zero', raw: NaN };
  const n = Math.round(w * 100) / 100;
  const sign = n >= 0 ? '+' : '';
  const inner = `${sign}${n.toFixed(2)}`;
  return { text: `(${inner})`, signClass: signClass(n), raw: n };
}

export function formatMoney(value, decimals = 2, signed = false) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '--';
  const sign = signed && num > 0 ? '+' : '';
  return `${sign}${num.toLocaleString('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
}

export function formatPercent(value, decimals = 2, signed = false) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '--';
  const sign = signed && num > 0 ? '+' : '';
  return `${sign}${(num * 100).toFixed(decimals)}%`;
}

export function signClass(value) {
  const num = Number(value);
  if (num > 0.0001) return 'pos';
  if (num < -0.0001) return 'neg';
  return 'zero';
}

function hash01(str) {
  let h = 2166136261;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/** 机构名称色调（对齐小程序 holding-hub agencyChipClass） */
export function agencyToneClass(name) {
  const s = String(name || '').trim();
  if (!s) return 'tone0';
  const idx = Math.floor(hash01(`agency:${s}`) * 12) % 12;
  return `tone${idx}`;
}
